'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, BarChart3, X,
  ArrowLeftRight, Settings, Store, CalendarDays, CalendarClock, LogOut,
  Shield, FileText, MessageCircle, ShoppingBag, Truck, Package, Users, Lightbulb, Instagram,
  UserCog, Rss,
} from 'lucide-react';
import { useCurrentBusiness } from '@/lib/business-context';
import { gamificationApi, marketplaceApi, adminApi, wholesaleApi, type GamificationStatus } from '@/lib/api';
import { BusinessLevel } from '@/components/gamification/business-level';
import { WebOnlyBillingAction } from '@/components/billing/web-only-billing-action';
import { useFeatureFlags, type FeatureFlagKey } from '@/lib/feature-flags';
import { signOut } from '@/lib/sign-out';
import { isSettingsRoute } from '@/lib/settings-route';

const MARKETPLACE_CONVERSATION_REFRESH_EVENT = 'marketplace:conversations-updated';

/** Hrefs that appear in the bottom nav — hidden from sidebar on mobile. */
const BOTTOM_NAV_HREFS = new Set(['/feed', '/marketplace', '/messages', '/dashboard']);

type NavSubItem = {
  name: string;
  /** Switches the active business first, then goes to the dashboard. A link
      alone would not change which store the dashboard renders. */
  businessId: string;
};

type NavItem = {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
  /** When set, the item only renders while this feature flag is on. */
  flag?: FeatureFlagKey;
  /**
   * Overrides the default href-prefix match. Needed wherever one entry owns
   * more than one route: several /dashboard/<x> pages are thin re-exports of
   * the top-level /<x> page, so a prefix match on /dashboard lit up My Stores
   * on /dashboard/settings and /dashboard/sales instead of the real entry.
   */
  match?: (pathname: string) => boolean;
};

type NavGroup = {
  group: string;
  items: NavItem[];
  /** When set, the whole group only renders while this feature flag is on. */
  flag?: FeatureFlagKey;
  /** Renders the group's items without its heading. */
  hideLabel?: boolean;
};

/** Matches /<x> and its /dashboard/<x> re-export. */
function withDashboardAlias(href: string) {
  const alias = `/dashboard${href}`;
  return (pathname: string) =>
    pathname === href ||
    pathname.startsWith(`${href}/`) ||
    pathname === alias ||
    pathname.startsWith(`${alias}/`);
}

const navigation: NavGroup[] = [
  { group: 'Main', items: [
    { name: 'Feed', href: '/feed', icon: Rss, flag: 'nav.feed', match: pathname => pathname === '/feed' },
    {
      name: 'Marketplace',
      href: '/marketplace',
      icon: Store,
      match: (pathname) =>
        pathname === '/marketplace' ||
        pathname.startsWith('/buy') ||
        pathname === '/products' ||
        pathname.startsWith('/products/') ||
        pathname.startsWith('/sell/catalog'),
    },
    {
      name: 'My Stores',
      href: '/dashboard',
      icon: LayoutDashboard,
      // The store pages only — the other /dashboard/* routes belong to the
      // Sales, Settings, Transactions, Invoices and Calendar entries.
      match: (pathname) =>
        pathname === '/dashboard' ||
        pathname === '/dashboard/products' ||
        pathname.startsWith('/dashboard/products/'),
    },
    { name: 'Messages', href: '/messages', icon: MessageCircle },
    { name: 'Reservations', href: '/reservations', icon: CalendarClock, flag: 'nav.reservations' },
    { name: 'Instagram', href: '/instagram', icon: Instagram },
  ]},
  { group: 'Money', items: [
    { name: 'Orders', href: '/orders', icon: ShoppingBag },
    { name: 'Sales', href: '/sales', icon: BarChart3, match: withDashboardAlias('/sales') },
    { name: 'Transactions', href: '/transactions', icon: ArrowLeftRight, flag: 'nav.transactions', match: withDashboardAlias('/transactions') },
    { name: 'Invoices', href: '/invoices', icon: FileText, flag: 'nav.invoices', match: withDashboardAlias('/invoices') },
  ]},
  { group: 'Schedule', flag: 'nav.schedule', items: [
    { name: 'Calendar', href: '/calendar', icon: CalendarDays, flag: 'nav.calendar', match: withDashboardAlias('/calendar') },
    { name: 'Pickup', href: '/sell/pickup', icon: Package },
    { name: 'Delivery', href: '/sell/delivery', icon: Truck },
    { name: 'Buyers', href: '/sell/customers', icon: Users },
    { name: 'Insights', href: '/sell/insights', icon: Lightbulb },
  ]},
  // Heading hidden for now. Settings stays: with the dashboard tab strip gone,
  // this is the only nav entry that reaches it. Sign Out renders in the mobile
  // drawer only — see below.
  { group: 'Account', hideLabel: true, items: [
    {
      // The Settings sub-tabs all link to /dashboard/settings/*, so entering
      // through that path keeps the inner tab strip in sync too. /settings/*
      // stays routable and re-exports the same pages.
      name: 'Settings',
      href: '/dashboard/settings/profile',
      icon: Settings,
      match: (pathname) =>
        (pathname.startsWith('/settings') || pathname.startsWith('/dashboard/settings')) &&
        !pathname.includes('/settings/account'),
    },
    {
      // Its own entry, not just the seventh tab inside Settings: App Store
      // review checks that account deletion is reachable in a couple of taps,
      // and the settings tab strip scrolls it off-screen on a phone.
      name: 'Account',
      href: '/dashboard/settings/account',
      icon: UserCog,
      match: (pathname) => pathname.includes('/settings/account'),
    },
  ]},
];

interface SidebarProps {
  /** Controlled open state for mobile (from bottom nav "More" tab). */
  mobileOpen?: boolean;
  /** Callback when mobile sidebar should close. */
  onMobileOpenChange?: (open: boolean) => void;
}

export function Sidebar({ mobileOpen, onMobileOpenChange }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [gamification, setGamification] = useState<GamificationStatus | null>(null);
  const [offerCount, setOfferCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const { currentBusinessId, businesses, loading: bizLoading, setCurrentBusinessId } = useCurrentBusiness();
  const { flags } = useFeatureFlags();

  // Deliberately NOT `currentBusinessId && businesses.length > 0` — that is the
  // exact condition that hid Orders in production. On a transient /businesses
  // failure the provider keeps currentBusinessId but leaves businesses empty and
  // loading false, and the top bar then shows "Sign In" to a signed-in user; the
  // sidebar is meant to be the entry point that survives that. currentBusinessId
  // is only ever set for a real session and is cleared on 401 and on sign out.
  const isLoggedIn = Boolean(currentBusinessId);
  const isOpen = mobileOpen ?? false;
  const closeMobile = useCallback(() => {
    onMobileOpenChange?.(false);
  }, [onMobileOpenChange]);

  useEffect(() => {
    adminApi.me().then((res) => setIsAdmin(res.isAdmin)).catch(() => {});
  }, []);

  const refreshUnreadMessageCount = useCallback(async () => {
    if (!currentBusinessId) {
      setUnreadMessageCount(0);
      return;
    }
    try {
      const res = await marketplaceApi.getUnreadMessageCount();
      setUnreadMessageCount(res.count);
    } catch {
      // Ignore unread count errors.
    }
  }, [currentBusinessId]);

  useEffect(() => {
    if (currentBusinessId) {
      gamificationApi.getStatus().then(setGamification).catch(() => {});
      // A count, not the history. This effect re-runs on every route change, and
      // deriving the badge from getOffers('seller') meant re-downloading the
      // seller's entire offer list (2.5 MB / 1,042 rows for a real seller) each
      // time, well past the 5s GET cache.
      marketplaceApi.getNavCounts()
        .then((res) => setOfferCount(res.actionable))
        .catch(() => {});
      void refreshUnreadMessageCount();
      wholesaleApi.getOverdueBuyers()
        .then((data) => setOverdueCount(Array.isArray(data) ? data.length : 0))
        .catch(() => {});
    } else {
      setUnreadMessageCount(0);
    }
  }, [currentBusinessId, pathname, refreshUnreadMessageCount]);

  useEffect(() => {
    if (!currentBusinessId) return;

    const onConversationUpdated = () => {
      void refreshUnreadMessageCount();
    };
    const intervalId = window.setInterval(() => {
      void refreshUnreadMessageCount();
    }, 30000);

    window.addEventListener(MARKETPLACE_CONVERSATION_REFRESH_EVENT, onConversationUpdated);
    return () => {
      window.removeEventListener(MARKETPLACE_CONVERSATION_REFRESH_EVENT, onConversationUpdated);
      window.clearInterval(intervalId);
    };
  }, [currentBusinessId, refreshUnreadMessageCount]);

  const handleSignOut = () => {
    void signOut();
  };

  // Built here rather than in the static `navigation` table: the My Stores
  // list depends on live state (the user's business list).
  const subItemsFor = (href: string): NavSubItem[] => {
    if (href === '/dashboard') {
      return businesses.map((b) => ({ name: b.name, businessId: b.id }));
    }
    return [];
  };

  const navContent = (isMobile: boolean) => (
    <>
      {/* Mobile only: on desktop the top bar already carries the logo, and the
          notification bell now lives there too — keeping this row would repeat
          the brand twice on the same screen. */}
      {/* Mobile: close row only — no logo. The brand already lives in the top
          bar, and in the native shell this row starts under the iOS status bar
          (viewport-fit=cover), so a wordmark here sat clipped behind the
          time/battery. The bell also stays in the top bar (a copy here would
          run a second 60s alert poll with diverging read state). */}
      {isMobile && (
        <div className="flex h-12 items-center justify-end border-b px-2">
          <Button variant="ghost" size="icon" onClick={closeMobile} aria-label="Close menu">
            <X className="h-5 w-5" />
          </Button>
        </div>
      )}
      <nav className="flex-1 overflow-y-auto px-3 pt-3 pb-4">
        {navigation.map((group) => {
          if (group.flag && !flags[group.flag]) return null;
          // On mobile, filter out items that are already in the bottom nav
          const enabledItems = group.items.filter((item) => !item.flag || flags[item.flag]);
          const items = isMobile
            ? enabledItems.filter((item) => !BOTTOM_NAV_HREFS.has(item.href))
            : enabledItems;
          if (items.length === 0 && group.group !== 'Account') return null;

          return (
            <div key={group.group} className="mb-3">
              {!group.hideLabel && (
                <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {group.group}
                </p>
              )}
              <div className="flex flex-col gap-0.5">
                {items.map((item) => {
                  const isActive = item.match
                    ? item.match(pathname)
                    : pathname === item.href || pathname.startsWith(item.href + '/');
                  const subs = subItemsFor(item.href);
                  return (
                    <div key={item.href} className="flex flex-col gap-0.5">
                    <Link
                      href={item.href}
                      prefetch={item.href === '/feed' ? false : undefined}
                      onClick={closeMobile}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-1.5 text-sm transition-colors',
                        isActive
                          ? 'bg-primary/10 text-foreground font-medium'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      <item.icon className={cn('h-4 w-4 shrink-0', isActive && 'text-primary')} />
                      <span className="flex-1">{item.name}</span>
                      {item.href === '/marketplace' && offerCount > 0 && (
                        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">
                          {offerCount}
                        </span>
                      )}
                      {item.name === 'Buyers' && overdueCount > 0 && (
                        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">
                          {overdueCount}
                        </span>
                      )}
                      {item.name === 'Messages' && unreadMessageCount > 0 && (
                        <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">
                          {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                        </span>
                      )}
                    </Link>
                    {subs.length > 0 && (
                      <div className="ml-[22px] flex flex-col gap-0.5 border-l pl-2">
                        {subs.map((sub) => {
                          const subActive =
                            sub.businessId === currentBusinessId && pathname.startsWith('/dashboard');
                          return (
                            <button
                              key={sub.businessId}
                              type="button"
                              className={cn(
                                'flex items-center gap-2 rounded-md px-2 py-1 text-[13px] transition-colors text-left',
                                subActive
                                  ? 'bg-primary/10 text-foreground font-medium'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                              )}
                              onClick={() => {
                                setCurrentBusinessId(sub.businessId);
                                closeMobile();
                                router.push('/dashboard');
                              }}
                            >
                              <span className="flex-1 truncate">{sub.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    </div>
                  );
                })}
                {group.group === 'Account' && isAdmin && (
                  <Link
                    href="/admin/dashboard"
                    onClick={closeMobile}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-3 py-1.5 text-sm transition-colors',
                      pathname.startsWith('/admin')
                        ? 'bg-destructive/10 text-destructive font-medium'
                        : 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
                    )}
                  >
                    <Shield className="h-4 w-4 shrink-0" />
                    <span className="flex-1">Admin</span>
                  </Link>
                )}
                {/* Mobile drawer only. On desktop the Settings header carries
                    Sign Out, but on mobile that drawer is the only path to it —
                    the bottom nav has no sign-out entry. */}
                {group.group === 'Account' && isMobile && (
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-3 rounded-md px-3 py-1.5 text-sm transition-colors text-muted-foreground hover:bg-destructive/10 hover:text-destructive w-full"
                  >
                    <LogOut className="h-4 w-4 shrink-0" />
                    <span className="flex-1 text-left">Sign Out</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </nav>
      {gamification && (
        <Link
          href="/settings/achievements"
          onClick={closeMobile}
          className="mx-3 mb-3 rounded-lg border bg-muted/30 p-3 hover:bg-muted/50 transition-colors block"
        >
          <BusinessLevel
            level={gamification.level.level}
            name={gamification.level.name}
            title={gamification.level.title}
            progress={gamification.levelProgress}
            nextLevelName={gamification.nextLevel?.name}
            compact
          />
        </Link>
      )}
      {/* Legal footer. Signed-in users reach the policies from here; the auth
          screens carry the same links for everyone else. */}
      <div className="mx-3 mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 pt-2 text-xs text-muted-foreground">
        <Link
          href="/about"
          onClick={closeMobile}
          className="hover:text-foreground hover:underline"
        >
          About
        </Link>
        {isSettingsRoute(pathname) && <WebOnlyBillingAction>
          <span aria-hidden="true">·</span>
          <Link
            href="/pricing"
            onClick={closeMobile}
            className="hover:text-foreground hover:underline"
          >
            Pricing
          </Link>
        </WebOnlyBillingAction>}
        <span aria-hidden="true">·</span>
        <Link
          href="/privacy"
          onClick={closeMobile}
          className="hover:text-foreground hover:underline"
        >
          Privacy
        </Link>
        <span aria-hidden="true">·</span>
        <Link
          href="/terms"
          onClick={closeMobile}
          className="hover:text-foreground hover:underline"
        >
          Terms
        </Link>
        <span aria-hidden="true">·</span>
        <Link
          href="/support"
          onClick={closeMobile}
          className="hover:text-foreground hover:underline"
        >
          Support
        </Link>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile scrim */}
      {isOpen && isLoggedIn && (
        <div className="drawer-scrim fixed inset-0 z-[60] bg-black/50 md:hidden" onClick={closeMobile} />
      )}
      {/* Mobile slide-in panel — mounted only while open. `md:hidden` is
          display-only, so an always-mounted panel put a second copy of every
          nav link, the Admin link and Sign Out into the DOM at every
          breakpoint: screen readers read the navigation twice and any
          getByRole('link') query matches two nodes.

          It enters from the *right*, under the "More" tab that opens it: a
          panel that flies in from the opposite edge reads as unrelated to the
          control that summoned it. Mounting already at its final position is
          why the old `transition-transform` never animated — the enter is a
          keyframe animation instead (see `.drawer-right`). */}
      {isOpen && isLoggedIn && (
        <aside
          id="app-nav-drawer"
          aria-label="Navigation"
          className="drawer-right fixed inset-y-0 right-0 z-[70] flex w-60 flex-col border-l bg-background md:hidden pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
        >
          {navContent(true)}
        </aside>
      )}
      {/* Desktop sidebar — same nav, always visible. It is a flex child of the
          shell row (not fixed), so `main` shrinks beside it instead of sliding
          underneath. Rendered with isMobile=false so it keeps the entries the
          mobile panel drops into the bottom nav — Orders in particular, which
          on desktop otherwise had exactly one entry point: a top-bar button
          that disappears whenever no business is selected. */}
      {/* Signed-in only. /marketplace and /checkout are public routes inside the
          (app) group, so an unconditional sidebar showed My Stores, Orders,
          Sales and a Sign Out button to anonymous visitors — every top-bar entry
          is gated the same way. */}
      {isLoggedIn && (
        <aside className="hidden md:flex w-60 flex-none flex-col border-r bg-background overflow-y-auto">
          {navContent(false)}
        </aside>
      )}
    </>
  );
}
